/**
 * Date formatting utilities.
 *
 * All dates from the API are in UTC. These helpers convert to the
 * app-configured display timezone before formatting.
 */

let displayTimezone = 'UTC';

/** Call once at boot with the timezone from page props. */
export function setDisplayTimezone(tz: string): void {
    displayTimezone = tz;
}

/** Format a UTC ISO string or date-only string as a localized date. */
export function formatDate(
    value: string,
    options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' },
    locale: string = 'en-US',
): string {
    const date = toDate(value);
    return date.toLocaleDateString(locale, { ...options, timeZone: displayTimezone });
}

/** Format a UTC ISO string as a localized time. */
export function formatTime(
    value: string,
    options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
    locale: string = 'en-US',
): string {
    const date = toDate(value);
    return date.toLocaleTimeString(locale, { ...options, timeZone: displayTimezone });
}

/** Format a UTC ISO string as date + time. */
export function formatDateTime(
    value: string,
    locale: string = 'en-GB',
): string {
    return `${formatDate(value, { day: 'numeric', month: 'short', year: 'numeric' }, locale)} ${formatTime(value, { hour: '2-digit', minute: '2-digit' }, locale)}`;
}

/** Relative time ago string. Falls back to absolute date for old entries. */
export function timeAgo(iso: string): string {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return 'unknown';
    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'yesterday';
    if (days < 14) return `${days}d ago`;
    return formatDate(iso);
}

/** Get today's date string (YYYY-MM-DD) in the display timezone. */
export function todayString(): string {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: displayTimezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const y = parts.find(p => p.type === 'year')!.value;
    const m = parts.find(p => p.type === 'month')!.value;
    const d = parts.find(p => p.type === 'day')!.value;
    return `${y}-${m}-${d}`;
}

/** Parse a value into a Date. Handles date-only strings by appending T12:00:00Z. */
function toDate(value: string): Date {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return new Date(value + 'T12:00:00Z');
    }
    return new Date(value);
}
